<template>
  <!-- Keep DOM (v-show) so map persists; fix size after showing -->
  <transition name="otm-fade" @after-enter="onModalShown">
    <div v-show="visible" class="otm-overlay" @click.self="close">
      <div class="otm-modal">
        <header class="otm-hdr">
          <div class="otm-hdr-left">
            <div class="otm-pill">En atención</div>
            <h3>{{ profesionalNombre || 'Profesional' }}</h3>
            <small v-if="ultimoPing" class="otm-sub">
              Último ping: {{ new Date(ultimoPing).toLocaleString() }}
            </small>
          </div>
          <button class="otm-x" @click="close" aria-label="Cerrar">×</button>
        </header>

        <div class="otm-map-wrap">
          <l-map
            ref="leaf"
            :key="mapKey"
            :options="{ attributionControl:false, zoomControl:true }"
            :zoom="zoom"
            :center="center"
          >
            <l-tile-layer :url="tileUrl" :attribution="''" />
            <!-- Destino (cliente) -->
            <l-marker v-if="destPos" :lat-lng="destPos" :icon="destIcon" />
            <!-- Profesional con foto -->
            <l-moving-rotated-marker
              v-if="profPos"
              :key="'prof-' + profIconKey"
              :lat-lng="profPos"
              :rotationAngle="angle"
              :duration="400"
              :icon="profIcon"
            />
          </l-map>

          <div class="otm-tools">
            <button @click="centerProf" :disabled="!profPos">Centrar profesional</button>
            <button @click="centerDest" :disabled="!destPos">Centrar destino</button>
            <!-- <a v-if="profPos" :href="googleMapsUrl" target="_blank" rel="noopener">Abrir en Google Maps</a> -->
          </div>
        </div>

        <footer class="otm-ftr">
          <div class="otm-legend">
            <span class="otm-ico otm-dest"></span> Destino
          </div>
          <div class="otm-grow"></div>
          <!-- <small class="otm-credit">© OpenStreetMap contributors</small> -->
          <button class="otm-secondary" @click="close">Cerrar</button>
        </footer>
      </div>
    </div>
  </transition>
</template>

<script>
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import { LMap, LTileLayer, LMarker } from 'vue2-leaflet'
import LMovingRotatedMarker from 'vue2-leaflet-moving-rotated-marker'
import io from 'socket.io-client'

export default {
  name: 'OrdenTrackingModalVue2Leaflet',
  components: { LMap, LTileLayer, LMarker, LMovingRotatedMarker },

  props: {
    visible: { type: Boolean, default: false },
    // Debe incluir token_atencion y profesional.foto
    orden: { type: Object, required: true },
    // Mover a <body> para evitar stacking-context detrás de menús
    appendToBody: { type: Boolean, default: true },
  },

  data () {
    return {
      tileUrl: 'https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png',
      zoom: 14,
      center: L.latLng(-33.45, -70.65),
      destPos: null,       // L.latLng
      profPos: null,       // L.latLng
      angle: 0,
      ultimoPing: null,
      socket: null,

      // destino: simple pin en azul (DivIcon)
      destIcon: L.divIcon({ className:'otm-dest-icon', html:'<div class="otm-pin"></div>' }),

      mapKey: 0, // opcional: forzar re-render del LMap si quisieras
    }
  },

  computed: {
    profesionalNombre () {
      const p = this.orden?.profesional || {}
      if (p.nombre || p.apellido) return [p.nombre, p.apellido].filter(Boolean).join(' ')
      return p.nombre_completo || ''
    },
    googleMapsUrl () {
      return this.profPos ? `https://www.google.com/maps?q=${this.profPos.lat},${this.profPos.lng}` : '#'
    },
    socketToken () {
      return this.orden && this.orden.token_atencion ? String(this.orden.token_atencion) : ''
    },

    // Foto del profesional
    profFotoUrl () {
      const f = this.orden && this.orden.profesional && this.orden.profesional.foto
      return (typeof f === 'string' && f.length) ? f : ''
    },

    // Ícono del profesional: avatar circular
    profIcon () {
      const url = this.profFotoUrl || 'data:image/svg+xml;utf8,' +
        encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" width="44" height="44"><circle cx="22" cy="22" r="22" fill="#9CA3AF"/></svg>')

      const html = `
        <div class="otm-avatar">
          <img src="${url}" alt="Profesional" />
        </div>
      `
      return L.divIcon({
        className: 'otm-avatar-icon',
        html,
        iconSize: [44, 44],
        iconAnchor: [22, 22]
      })
    },

    // Para refrescar marker si cambia la foto
    profIconKey () {
      return this.profFotoUrl || 'no-foto'
    },
  },

  watch: {
    visible (v) {
      if (v) {
        // ensure at <body> to avoid stacking issues
        if (this.appendToBody && this.$el && this.$el.parentNode !== document.body) {
          document.body.appendChild(this.$el)
        }
        // lock scroll
        document.documentElement.style.overflow = 'hidden'
        document.body.style.overflow = 'hidden'

        // (re)connect socket con token actual
        this.reconnectSocketIfNeeded()

        // reflow Leaflet after visible
        this.$nextTick(() => this.scheduleInvalidate())
        // this.mapKey++ // si prefieres recrear LMap en cada apertura
      } else {
        // unlock scroll y cerrar socket
        document.documentElement.style.overflow = ''
        document.body.style.overflow = ''
        this.closeSocket()
      }
    },

    // Reconectar si el token cambia mientras está abierto
    'orden.token_atencion' () {
      if (this.visible) this.reconnectSocketIfNeeded(true)
    },

    // Recalcular destino si cambia la orden
    orden: {
      deep: true,
      handler () { this.computeDestination() }
    }
  },

  mounted () {
    if (this.appendToBody && this.$el && this.$el.parentNode !== document.body) {
      document.body.appendChild(this.$el)
    }
    this.computeDestination()
    if (this.visible) {
      this.reconnectSocketIfNeeded()
      this.$nextTick(() => this.scheduleInvalidate())
    }
  },

  beforeDestroy () {
    this.closeSocket()
    document.documentElement.style.overflow = ''
    document.body.style.overflow = ''
  },

  methods: {
    // -------- Destination from Orden --------
    computeDestination () {
      const o = this.orden || {}
      const la = o.lat ?? o.dest_lat ?? o.destino_lat ?? o?.destino?.lat
      const lo = o.lon ?? o.dest_lng ?? o.destino_lng ?? o?.destino?.lng
      this.destPos = (isFinite(la) && isFinite(lo)) ? L.latLng(Number(la), Number(lo)) : null
      if (this.destPos) this.center = this.destPos
    },

    // -------- Socket handling --------
    getSocketUrl () { return (typeof window !== 'undefined' && window.socket) ? window.socket : '' },

    reconnectSocketIfNeeded (force = false) {
      const url = this.getSocketUrl()
      if (!url || !this.socketToken) {
        this.closeSocket()
        return
      }
      if (force) this.closeSocket()
      if (this.socket) return // ya conectado

      this.socket = io(url, { extraHeaders: { token: this.socketToken, tipo: 'cliente' } })

      this.socket.on('escuchar_info', raw => {
        const d = typeof raw === 'string' ? JSON.parse(raw) : raw
        if (!isFinite(d.lat) || !isFinite(d.lon)) return
        const next = L.latLng(Number(d.lat), Number(d.lon))

        // heading suavizado
        if (this.profPos) {
          const dy = next.lat - this.profPos.lat
          const dx = next.lng - this.profPos.lng
          const rad = Math.atan2(dx, dy)
          const deg = rad * 180 / Math.PI
          if (!isNaN(deg)) this.angle = deg
        }

        this.profPos = next
        this.ultimoPing = d.ping_at || new Date().toISOString()
        this.fitBoth()
      })

      this.socket.on('terminar_orden', () => {
        this.$emit('terminada', this.orden)
        this.close()
      })
    },

    closeSocket () {
      if (this.socket) {
        try { this.socket.close() } catch (e) {}
        this.socket = null
      }
    },

    // -------- Map helpers --------
    onModalShown () { this.scheduleInvalidate() },

    scheduleInvalidate () {
      // Double RAF para asegurar layout pintado antes de recalcular Leaflet
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const map = this.$refs.leaf && this.$refs.leaf.mapObject
          if (map) {
            map.invalidateSize(true)
            this.fitBoth()
          }
        })
      })
    },

    fitBoth () {
      const map = this.$refs.leaf && this.$refs.leaf.mapObject
      if (!map) return

      if (this.profPos && this.destPos) {
        const b = L.latLngBounds([this.profPos, this.destPos])
        map.fitBounds(b, { padding:[40,40] })
      } else if (this.profPos) {
        map.setView(this.profPos, 16)
      } else if (this.destPos) {
        map.setView(this.destPos, 16)
      }
    },

    centerProf () {
      const map = this.$refs.leaf && this.$refs.leaf.mapObject
      if (map && this.profPos) map.setView(this.profPos, 16)
    },
    centerDest () {
      const map = this.$refs.leaf && this.$refs.leaf.mapObject
      if (map && this.destPos) map.setView(this.destPos, 16)
    },

    // -------- Close --------
    close () {
      this.$emit('update:visible', false)
      this.$emit('cerrar')
    },
  }
}
</script>

<style scoped>
/* Overlay & modal */
.otm-overlay{
  position: fixed; inset: 0;
  display: grid; place-items: center;
  background: rgba(17,24,39,.55);
  z-index: 2147483647;
}
.otm-modal{
  width: 70vw;
  height: 80vh;
  background:#fff; border-radius:14px;
  display:flex; flex-direction:column; overflow:hidden;
  box-shadow:0 20px 60px rgba(0,0,0,.25);
}

/* Header */
.otm-hdr{display:flex;align-items:center;padding:14px 16px;border-bottom:1px solid #e5e7eb}
.otm-hdr-left{display:flex;flex-direction:column;gap:2px}
.otm-hdr h3{margin:0;font-size:16px;color:#111827}
.otm-sub{color:#6b7280}
.otm-pill{display:inline-flex;align-items:center;gap:6px;background:#ecfdf5;color:#065f46;border:1px solid #d1fae5;border-radius:999px;padding:4px 8px;font-size:12px;font-weight:700;width:max-content;margin-bottom:4px}
.otm-x{margin-left:auto;width:36px;height:36px;font-size:22px;line-height:36px;border:0;background:transparent;cursor:pointer;color:#6b7280}

/* Map area */
.otm-map-wrap{position:relative;flex:1}
.leaflet-container{position:absolute;inset:0; z-index:0} /* Fill and sit below tools */
.otm-tools{
  position:absolute; top:12px; left:12px;
  display:flex; gap:8px;
  background:rgba(255,255,255,.92);
  border:1px solid #e5e7eb; border-radius:10px; padding:8px;
  z-index:10000;
}
.otm-tools button{border:1px solid #e5e7eb;background:#fff;border-radius:8px;padding:6px 10px;cursor:pointer}

/* Footer */
.otm-ftr{display:flex;align-items:center;gap:12px;padding:10px 14px;border-top:1px solid #e5e7eb}
.otm-legend{display:flex;align-items:center;gap:10px;color:#6b7280;font-size:12px}
.otm-ico{width:10px;height:10px;border-radius:999px;display:inline-block}
.otm-dest{background:#2563eb}
.otm-sep{color:#d1d5db}
.otm-grow{flex:1}
.otm-credit{color:#9ca3af;font-size:11px;margin-right:8px}
.otm-secondary{border:1px solid #e5e7eb;background:#fff;border-radius:10px;padding:8px 12px;cursor:pointer}

/* Fade transition */
.otm-fade-enter-active,.otm-fade-leave-active{transition:opacity .15s ease}
.otm-fade-enter,.otm-fade-leave-to{opacity:0}
</style>

<!-- Global (unscoped) — needed so Leaflet panes pick up styles -->
<style>
/* Avatar marker (professional) */
.otm-avatar{
  width:44px; height:44px;
  border-radius:50%;
  overflow:hidden;
  border:2px solid #fff;
  box-shadow:0 6px 16px rgba(0,0,0,.28);
  background:#e5e7eb;
}
.otm-avatar img{
  width:100%; height:100%;
  object-fit:cover; display:block;
}

/* Destination pin */
.otm-dest-icon .otm-pin{
  width:14px; height:14px;
  border-radius:4px;
  transform:rotate(45deg);
  background:#2563eb;
  border:2px solid #fff;
}
</style>
